// playerEvolution — pure derivation over the weekly playerHistory ring
// buffer: trend, 4-week delta and a projected peak. Computed on render;
// no system needed.

import type { Player, PlayerSnapshot } from '@/config/playersConfig'

export type Trend = 'progresando' | 'estancado' | 'en declive'

export interface Evolution {
  trend: Trend
  /** Level change across (up to) the last 4 weekly snapshots */
  levelDelta4w: number
  /** Where the player is heading: current pace extrapolated to age 33,
   *  capped by potential; never below the current level */
  projectedPeak: number
  /** Chronological history for the sparkline */
  history: PlayerSnapshot[]
}

const DECLINE_AGE = 33
const DAYS_PER_MONTH = 28

/** Needs at least 2 snapshots; returns null before that. */
export function getEvolution(history: PlayerSnapshot[] | undefined, player: Player): Evolution | null {
  if (!history || history.length < 2) return null

  const recent = history.slice(-4)
  const levelDelta4w = recent[recent.length - 1].level - recent[0].level

  const trend: Trend = levelDelta4w > 0 ? 'progresando'
    : levelDelta4w < 0 ? 'en declive'
    : 'estancado'

  // Pace from the whole buffer: level points per month
  const first = history[0]
  const last = history[history.length - 1]
  const spanMonths = Math.max(1 / 4, (last.day - first.day) / DAYS_PER_MONTH)
  const pacePerMonth = (last.level - first.level) / spanMonths

  const monthsUntilDecline = Math.max(0, (DECLINE_AGE - player.age) * 12)
  const projectedRaw = player.level + Math.max(0, pacePerMonth) * monthsUntilDecline
  const projectedPeak = Math.max(player.level, Math.min(player.potential, Math.round(projectedRaw)))

  return { trend, levelDelta4w, projectedPeak, history }
}

export const TREND_LABELS: Record<Trend, string> = {
  progresando: 'Progresando',
  estancado: 'Estancado',
  'en declive': 'En declive',
}
